import { XMLParser } from "fast-xml-parser";

import { FileRecord, FileService } from "@/shared/domain/files";
import { isAbortError } from "@/shared/errors";
import { HttpClient, HttpResponse } from "@/shared/http";
import { AsyncEvent } from "@/shared/synchronization";

import { LocalFile } from "./LocalFile";
import type { Numeric, Readable } from "./Readable";
import { StorageManager } from "./StorageManager";

/**
 * Determined experimentally
 */
interface S3Error {
  Error: {
    Code: string;
    Message: string;
    Expires: string;
    ServerTime: string;
    RequestId: string;
    HostId: string;
  };
}

export class RemoteFile implements Readable {
  private fileRecord: FileRecord;
  private fileService: FileService;
  private httpClient: HttpClient;
  private isPresignedUrlLoading = false;
  private presignedUrl: URL | undefined;
  private presignedUrlLoadedEvent = new AsyncEvent();

  constructor(
    fileRecord: FileRecord,
    fileService: FileService,
    httpClient?: HttpClient,
  ) {
    this.fileRecord = fileRecord;
    this.fileService = fileService;
    this.httpClient = httpClient ?? new HttpClient();
  }

  public id(): string {
    return `${this.fileRecord.file_id}#${this.fileRecord.version}`;
  }

  async read(
    offset: Numeric,
    size: Numeric,
    signal?: AbortSignal,
  ): Promise<Uint8Array> {
    const url = await this.getPresignedUrl(signal);
    const response = await this.httpClient.get(url, {
      excludeAuth: true,
      ranges: [
        {
          unit: "bytes",
          start: Number(offset),
          end: Number(offset) + Number(size) - 1, // Range requests are inclusive of bounds
        },
      ],
      signal,
    });

    if (await this.presignedUrlExpired(response)) {
      signal?.throwIfAborted();

      if (this.presignedUrl === undefined || this.isPresignedUrlLoading) {
        // If a concurrent request has already determined that the presigned URL has expired,
        // avoid stepping on its toes.
        // This AsyncEvent is set when the presigned URL has been successfully refreshed.
        await this.presignedUrlLoadedEvent.wait(signal);
        signal?.throwIfAborted();
      } else {
        // When RemoteFile::getPresignedUrl is called on the next call to RemoteFile::read,
        // it will refresh the presigned URL and set the AsyncEvent.
        this.presignedUrl = undefined;
        this.presignedUrlLoadedEvent.clear();
      }
      return this.read(offset, size, signal);
    }
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
  }

  public size(): Promise<bigint> {
    return Promise.resolve(BigInt(this.fileRecord.size));
  }

  /**
   * Request all bytes of the file in batches no larger than 1MB,
   * and write them into a file stored in the Origin Private Filesystem.
   * Return a LocalFile to provide a reader interface for those bytes.
   */
  public async toLocalFile(opts?: {
    path?: LocalFile;
    signal?: AbortSignal;
  }): Promise<LocalFile> {
    const file =
      opts?.path ??
      new LocalFile({
        // Default: <file_id>/<vN>/<filename>
        name: this.getFileRecordFileName(),
        parents: [this.fileRecord.file_id, `v${this.fileRecord.version}`],
      });

    if (await file.exists(this.fileRecord.size, opts?.signal)) {
      return file;
    }
    opts?.signal?.throwIfAborted();

    file.lastAccessTime = null;

    try {
      const fileHandle = await file.getWriteableSyncFileHandle(opts?.signal);
      opts?.signal?.throwIfAborted();
      try {
        // Memorialize last access time before downloading bytes.
        // If there is an issue downloading the data,
        // this will ensure the last access time file exists,
        // making it visible for GC.
        await file.memorializeLastAccessTime(opts?.signal);
        opts?.signal?.throwIfAborted();

        const MAX_CHUNK_SIZE = 1024 * 1024; // 1MiB chunks
        const numParts = Math.max(
          Math.ceil(this.fileRecord.size / MAX_CHUNK_SIZE),
          1,
        );

        // Create array of chunk ranges
        const ranges: { start: number; end: number }[] = [];
        for (let i = 0; i < numParts; i++) {
          ranges.push({
            start: i * MAX_CHUNK_SIZE,
            end: Math.min((i + 1) * MAX_CHUNK_SIZE, this.fileRecord.size),
          });
        }

        // Fetch all chunks concurrently and write them to the file
        await Promise.all(
          ranges.map(async (range) => {
            opts?.signal?.throwIfAborted();

            const chunk = await this.read(
              BigInt(range.start),
              BigInt(range.end - range.start),
              opts?.signal,
            );
            const numBytesWritten = fileHandle.write(chunk, {
              at: range.start,
            });
            if (numBytesWritten !== chunk.byteLength) {
              throw new Error("Failed to download data, please retry.", {
                cause: `Expected to write ${chunk.byteLength} bytes to ${file.toString()}, wrote ${numBytesWritten}`,
              });
            }
          }),
        );
        fileHandle.flush();
        return file;
      } finally {
        fileHandle.close();
      }
    } catch (err: unknown) {
      // Assume any exception downloading the data makes the file unusable
      try {
        await file.delete();
      } catch {
        // swallow, may happen if the file failed to create in the first place
      }

      const isDOMException = err instanceof DOMException;
      if (!isDOMException) {
        // unhandled, rethrow
        throw err;
      }

      // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemSyncAccessHandle/write#exceptions
      if (err.name === "QuotaExceededError") {
        await StorageManager.purgeStorage();
        throw new Error(
          [
            "Failed to download data.",
            "Roboto uses temporary storage on your file system, which is now full.",
            "We've cleared our stored data, but you may need to free additional space.",
            "Check your available storage then reload this panel to retry.",
          ].join(" "),
          {
            cause: err,
          },
        );
      }

      if (isAbortError(err)) {
        throw err;
      }

      // in any other case, just ask the user to retry
      // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileHandle/createSyncAccessHandle#exceptions
      throw new Error(
        [
          "Failed to download data.",
          "Roboto uses temporary storage on your file system.",
          "Check your available storage then reload this panel to retry.",
          "You may see this error if using your browser in a 'private browsing' mode.",
        ].join(" "),
        {
          cause: err,
        },
      );
    }
  }

  public toString(): string {
    return `RemoteFile(${this.id()})`;
  }

  private getFileRecordFileName() {
    const parts = this.fileRecord.relative_path.split("/");
    return parts[parts.length - 1];
  }

  private async getPresignedUrl(signal?: AbortSignal): Promise<URL> {
    if (this.isPresignedUrlLoading) {
      await this.presignedUrlLoadedEvent.wait(signal);
    }
    if (this.presignedUrl === undefined) {
      this.isPresignedUrlLoading = true;
      try {
        const url = await this.fileService.getPresignedDownloadUrl(
          this.fileRecord.file_id,
          {
            abortSignal: signal,
          },
        );
        this.presignedUrl = url;
      } finally {
        this.isPresignedUrlLoading = false;
        this.presignedUrlLoadedEvent.set();
      }
    }

    return this.presignedUrl;
  }

  /**
   * Reading of remote files relies on having a presigned url that has not expired.
   * When the URLs expire, AWS returns a 403 with an XML document.
   */
  private async presignedUrlExpired(response: HttpResponse): Promise<boolean> {
    if (response.status !== 403) {
      return false;
    }

    // The signed URL might be expired.
    // AWS returns an XML document that looks like:
    // <Error>
    //    <Code>AccessDenied</Code>
    //    <Message>Request has expired</Message>
    //    <X-Amz-Expires>60</X-Amz-Expires>
    //    <Expires>2024-02-29T18:46:34Z</Expires>
    //    <ServerTime>2024-02-29T18:46:38Z</ServerTime>
    //    <RequestId>BNZ7FV4AWSPFW125</RequestId>
    //    <HostId>...</HostId>
    // </Error>
    const responseText = await response.text();
    const xmlParser = new XMLParser();
    const responseXML = xmlParser.parse(responseText) as S3Error;
    const error = responseXML["Error"];
    if (error === undefined) {
      return false;
    }

    const serverTime = Date.parse(error["ServerTime"] ?? "");
    const expirationTime = Date.parse(error["Expires"] ?? "");
    if (Number.isNaN(serverTime) || Number.isNaN(expirationTime)) {
      throw new Error("Failed to read file content", { cause: responseText });
    }
    return serverTime > expirationTime;
  }
}
